/**
 * PersonalizedPreview Component
 * 
 * Philosophy: Leverage strengths to unlock harder concepts
 * - Strong in geometry? Use that intuition to explore function shapes
 * - Strong in number sense? Use that to recognize rate patterns
 * - Arithmetic is Rhode Island; mathematical thinking is the universe
 */

import React, { useState } from 'react';
import { StretchChallenge } from './StretchChallenge';

interface SkillVector {
  number_sense: number;
  ratios_rates: number;
  linear_relationships: number;
  functions: number;
  geometry: number;
  data_probability: number;
}

interface PersonalizedPreviewProps {
  childName: string;
  skillVector: SkillVector;
  assessedGradeLevel?: number; // e.g., 2 for 2nd grade
  onClose?: () => void;
  onLinkRoblox?: () => void;
  onSetBudget?: () => void;
}

// Strength → Advanced exploration mapping
const STRENGTH_EXPLORATIONS: Record<keyof SkillVector, {
  emoji: string;
  advancedTopic: string;
  sampleQuestion: { text: string; reward: number };
  insight: string;
}> = {
  number_sense: {
    emoji: '🔢',
    advancedTopic: 'Pattern Recognition',
    sampleQuestion: {
      text: 'The sequence goes 2, 6, 12, 20, 30... What comes next? Is the growth constant?',
      reward: 35,
    },
    insight: 'Strong number sense → recognize when rates of change themselves change',
  },
  ratios_rates: {
    emoji: '⚡',
    advancedTopic: 'Rate of Change',
    sampleQuestion: {
      text: 'Your car goes 10m in the first second, 30m total by second 2, 60m by second 3. Is your speed constant?',
      reward: 40,
    },
    insight: 'Strong ratio intuition → understand acceleration vs constant speed',
  },
  linear_relationships: {
    emoji: '📈',
    advancedTopic: 'Beyond Linear',
    sampleQuestion: {
      text: 'Plot shows coins at t=1,2,3 are 10, 40, 90. Is this linear? What type of growth?',
      reward: 45,
    },
    insight: 'Understands y = mx + b → ready to explore y = ax² + bx + c',
  },
  functions: {
    emoji: '🔄',
    advancedTopic: 'Function Behavior',
    sampleQuestion: {
      text: 'f(x) = x² and g(x) = 2x. At what point does f(x) overtake g(x)?',
      reward: 50,
    },
    insight: 'Gets function notation → ready for comparing growth rates',
  },
  geometry: {
    emoji: '📐',
    advancedTopic: 'Visual Function Analysis',
    sampleQuestion: {
      text: 'This curve gets steeper as x increases. Is it linear, quadratic, or exponential?',
      reward: 45,
    },
    insight: 'Strong spatial reasoning → visualize how functions behave',
  },
  data_probability: {
    emoji: '📊',
    advancedTopic: 'Statistical Reasoning',
    sampleQuestion: {
      text: 'Your average XP is 25. You got 40 on the last battle. Will this pull your average up or down, and by how much?',
      reward: 40,
    },
    insight: 'Gets averages → ready for weighted means and variance',
  },
};

// Foundational skills that benefit from more practice
const FOUNDATION_QUESTIONS: Record<keyof SkillVector, {
  emoji: string;
  scenarioName: string;
  question: { text: string; reward: number };
}> = {
  number_sense: {
    emoji: '🌱',
    scenarioName: 'Garden Coins',
    question: { text: 'You have 5 groups of 7 coins. How many total?', reward: 15 },
  },
  ratios_rates: {
    emoji: '🏎️',
    scenarioName: 'Turbo Racer',
    question: { text: 'Your car travels 60 meters in 3 seconds. What is your speed?', reward: 20 },
  },
  linear_relationships: {
    emoji: '🌱',
    scenarioName: 'Garden Coins',
    question: { text: 'Your coins follow C = 10t + 5. How many coins at t = 6?', reward: 20 },
  },
  functions: {
    emoji: '⚔️',
    scenarioName: 'XP Grinder',
    question: { text: 'If f(m) = 25m, how much XP for defeating 4 monsters?', reward: 20 },
  },
  geometry: {
    emoji: '🏗️',
    scenarioName: 'Tower Builder',
    question: { text: 'Your tower has 4 sides, each 9 units. What is the perimeter?', reward: 20 },
  },
  data_probability: {
    emoji: '⚔️',
    scenarioName: 'XP Grinder',
    question: { text: 'Your last 5 battles gave 12, 18, 15, 20, 10 XP. What is the average?', reward: 20 },
  },
};

const SKILL_DISPLAY_NAMES: Record<keyof SkillVector, string> = {
  number_sense: 'Number Sense',
  ratios_rates: 'Ratios & Rates',
  linear_relationships: 'Linear Relationships',
  functions: 'Functions',
  geometry: 'Geometry',
  data_probability: 'Data & Probability',
};

export function PersonalizedPreview({
  childName,
  skillVector,
  assessedGradeLevel = 2, // Default to 2nd grade
  onClose,
  onLinkRoblox,
  onSetBudget,
}: PersonalizedPreviewProps) {
  const [showStretchChallenge, setShowStretchChallenge] = useState(false);
  const [stretchCompleted, setStretchCompleted] = useState<{ passed: boolean; reward: number } | null>(null);

  // Find strongest skills (sorted descending)
  const sortedSkills = Object.entries(skillVector)
    .sort(([, a], [, b]) => b - a) as [keyof SkillVector, number][];
  
  const strongestSkills = sortedSkills.slice(0, 2);
  const growthAreas = sortedSkills.slice(-2);
  
  // Calculate recommended budget
  // Higher for advanced exploration, base for foundations
  const avgSkill = Object.values(skillVector).reduce((a, b) => a + b, 0) / 6;
  const recommendedBudget = Math.round(75 + (avgSkill * 150)); // 75-225 range - higher skill = more advanced = higher rewards
  
  // Build challenge mix: 60% strength-based advancement, 40% foundations
  const advancementChallenge = STRENGTH_EXPLORATIONS[strongestSkills[0][0]];
  const foundationChallenge = FOUNDATION_QUESTIONS[growthAreas[0][0]];

  const stretchLevel = Math.min(12, assessedGradeLevel * 2);
  const stretchReward = stretchLevel >= 10 ? 150 : stretchLevel >= 8 ? 100 : 75;

  // Show Stretch Challenge full screen
  if (showStretchChallenge) {
    return (
      <StretchChallenge
        childName={childName}
        assessedGradeLevel={assessedGradeLevel}
        onComplete={(result) => {
          setStretchCompleted({
            passed: result.passed,
            reward: result.passed ? stretchReward * 3 : 0,
          });
          setShowStretchChallenge(false);
        }}
        onClose={() => setShowStretchChallenge(false)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="text-center">
        <div className="text-5xl mb-3">🚀</div>
        <h3 className="text-xl font-bold text-white mb-1">
          {childName}'s Learning Path Ready!
        </h3>
        <p className="text-slate-400 text-sm">
          Leveraging strengths to unlock advanced concepts
        </p>
      </div>

      {/* Skill Profile */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs text-emerald-400 uppercase tracking-wide font-semibold">
              🌟 Strengths to Leverage
            </span>
            <div className="flex flex-wrap gap-2 mt-2">
              {strongestSkills.map(([skill, value]) => (
                <span 
                  key={skill}
                  className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full"
                >
                  {SKILL_DISPLAY_NAMES[skill]} ({Math.round(value * 100)}%)
                </span>
              ))}
            </div>
          </div>
          <div>
            <span className="text-xs text-blue-400 uppercase tracking-wide font-semibold">
              📚 Building Foundations
            </span>
            <div className="flex flex-wrap gap-2 mt-2">
              {growthAreas.map(([skill, value]) => (
                <span 
                  key={skill}
                  className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full"
                >
                  {SKILL_DISPLAY_NAMES[skill]} ({Math.round(value * 100)}%)
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recommended Budget */}
      <div className="bg-gradient-to-r from-blox-accent/20 to-purple-500/20 rounded-lg p-4 border border-blox-accent/30">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-slate-400 uppercase tracking-wide">Recommended Weekly Budget</span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-3xl font-bold text-white">{recommendedBudget}</span>
              <span className="text-blox-accent font-semibold">Robux</span>
            </div>
          </div>
          <div className="text-right text-sm text-slate-400">
            <div>60% advanced challenges</div>
            <div>40% building foundations</div>
          </div>
        </div>
      </div>

      {/* Challenge Preview */}
      <div>
        <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <span className="text-blox-accent">⚡</span>
          Sample Challenge Mix
        </h4>
        <div className="space-y-3">
          {/* Advanced challenge from strength */}
          <div className="bg-slate-800/80 rounded-lg p-4 border-l-4 border-emerald-500">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">{advancementChallenge.emoji}</span>
                  <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded font-semibold">
                    ADVANCEMENT
                  </span>
                  <span className="text-xs text-slate-500">{advancementChallenge.advancedTopic}</span>
                </div>
                <p className="text-white text-sm">{advancementChallenge.sampleQuestion.text}</p>
                <p className="text-xs text-slate-500 mt-2 italic">
                  💡 {advancementChallenge.insight}
                </p>
              </div>
              <div className="text-right shrink-0">
                <div className="text-emerald-400 font-bold">+{advancementChallenge.sampleQuestion.reward}</div>
                <div className="text-xs text-slate-500">Robux</div>
              </div>
            </div>
          </div>

          {/* Foundation challenge */}
          <div className="bg-slate-800/80 rounded-lg p-4 border-l-4 border-blue-500">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">{foundationChallenge.emoji}</span>
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded font-semibold">
                    FOUNDATION
                  </span>
                  <span className="text-xs text-slate-500">{foundationChallenge.scenarioName}</span>
                </div>
                <p className="text-white text-sm">{foundationChallenge.question.text}</p>
              </div>
              <div className="text-right shrink-0">
                <div className="text-blue-400 font-bold">+{foundationChallenge.question.reward}</div>
                <div className="text-xs text-slate-500">Robux</div>
              </div>
            </div>
          </div>
        </div>
        
        <p className="text-xs text-slate-500 mt-3 italic text-center">
          "Arithmetic is Rhode Island. Mathematical thinking is the universe."
        </p>
      </div>

      {/* Stretch Bonus Challenge */}
      <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-lg p-4 border border-amber-500/30">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">🏆</span>
          <div>
            <h4 className="text-sm font-bold text-amber-400">Stretch Bonus Available!</h4>
            <p className="text-xs text-slate-400">2x grade level = 3x Robux reward</p>
          </div>
        </div>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🧮</span>
                <span className="px-2 py-0.5 bg-amber-500/30 text-amber-300 text-xs rounded font-bold">
                  STRETCH
                </span>
                <span className="text-xs text-amber-400/70">+4 Grade Levels</span>
              </div>
              <p className="text-white text-sm">
                The path of a ball follows h(t) = -16t² + 48t + 4. At what time does it reach maximum height?
              </p>
              <p className="text-xs text-slate-500 mt-2">
                🎯 Parabolas & Optimization • Requires web verification
              </p>
            </div>
            <div className="text-right shrink-0">
              <div className="text-amber-400 font-bold text-lg">+150</div>
              <div className="text-xs text-amber-400/70">Robux</div>
              <div className="text-xs text-slate-500 mt-1">3x bonus!</div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs text-slate-400">
          <div className="flex items-center gap-1">
            <span>🔐</span>
            <span>Verified on web with parent present</span>
          </div>
          <div className="flex items-center gap-1">
            <span>🎥</span>
            <span>Show your work</span>
          </div>
        </div>

        <button
          className="w-full mt-3 py-2 px-4 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 font-semibold rounded-lg transition-colors border border-amber-500/30"
          onClick={() => setShowStretchChallenge(true)}
        >
          🚀 Try Stretch Challenge
        </button>
      </div>

      {/* Stretch Challenge Completion Notice */}
      {stretchCompleted && (
        <div className={`rounded-lg p-4 ${
          stretchCompleted.passed 
            ? 'bg-emerald-500/20 border border-emerald-500/30' 
            : 'bg-slate-800/50 border border-slate-700'
        }`}>
          {stretchCompleted.passed ? (
            <div className="flex items-center gap-3">
              <span className="text-3xl">🎉</span>
              <div>
                <p className="text-emerald-400 font-bold">Stretch Challenge Complete!</p>
                <p className="text-sm text-slate-300">
                  {childName} earned <span className="text-emerald-400 font-bold">+{stretchCompleted.reward} Robux</span> for solving a problem {Math.min(12, assessedGradeLevel * 2) - assessedGradeLevel} grade levels above their assessment!
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-3xl">💪</span>
              <div>
                <p className="text-slate-300 font-semibold">Great attempt!</p>
                <p className="text-sm text-slate-400">
                  Stretch challenges are meant to push boundaries. Keep practicing!
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* How It Works */}
      <div className="bg-slate-800/30 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-slate-300 mb-3">How It Works in Roblox</h4>
        <div className="grid grid-cols-3 gap-3 text-center text-xs">
          <div className="p-2">
            <div className="text-2xl mb-1">🎮</div>
            <div className="text-slate-400">{childName} plays AI Emperor</div>
          </div>
          <div className="p-2">
            <div className="text-2xl mb-1">🧠</div>
            <div className="text-slate-400">Strength-based challenges push boundaries</div>
          </div>
          <div className="p-2">
            <div className="text-2xl mb-1">💰</div>
            <div className="text-slate-400">Earns real Robux for growth</div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        {onLinkRoblox && (
          <button
            onClick={onLinkRoblox}
            className="flex-1 py-3 px-4 bg-blox-accent hover:bg-blox-accent/80 text-white font-semibold rounded-lg transition-colors"
          >
            🔗 Link Roblox Account
          </button>
        )}
        {onSetBudget && (
          <button
            onClick={onSetBudget}
            className="flex-1 py-3 px-4 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg transition-colors"
          >
            💰 Set Weekly Budget
          </button>
        )}
        {onClose && !onLinkRoblox && !onSetBudget && (
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 bg-blox-accent hover:bg-blox-accent/80 text-white font-semibold rounded-lg transition-colors"
          >
            Done
          </button>
        )}
      </div>
    </div>
  );
}

export default PersonalizedPreview;
